import { ChangeEvent, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { useTitle } from 'react-use';
import cn from 'classnames';
import { Form, FormRenderProps } from 'react-final-form';
import { useParams } from 'react-router';

import { captchaSetActionAfterTokenLoad, captchaSetIsActive } from 'CorePackage/CaptchaModule/actions/captchaActions';
import { getCaptchaToken } from 'CorePackage/CaptchaModule/selectors/captchaSelectors';
import { coreHideNotices } from 'CorePackage/MainModule/actions/coreActions';
import { composeValidators } from 'CorePackage/MainModule/common/composeValidators';
import { Layout } from 'CorePackage/MainModule/components/Layout';
import { DESKTOP_MIN } from 'CorePackage/MainModule/constants/breakpoints';
import { ROUTE_SEARCH, ROUTE_OPEN_DATA, ROUTE_PORTAL } from 'CorePackage/MainModule/constants/routes';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/makeMaxCharactersValidator';
import { makeMinCharactersValidator } from 'CorePackage/MainModule/validators/makeMinCharactersValidator';
import { NewsContainer } from 'NewsPackage/MainModule/components/NewsContainer';
import { searchSetSearchString, searchGetResultData } from 'SearchPackage/MainModule/actions/searchActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { PublicationWarningModal } from 'CorePackage/MainModule/components/PublicationWarningModal';

import { PublicMainLawInfo } from './PublicMainLawInfo';
import { PublicMainNonExistentQrModal } from './PublicMainNonExistentQrModal';
import styles from './PublicMainPage.module.scss';

interface IPublicMainPageStoreProps {
  captchaToken: string | null;
}

interface IPublicMainForm {
  searchString?: string;
}

const PublicMainPageWithoutStore = ({ captchaToken }: IPublicMainPageStoreProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const mt = new URLSearchParams(window.location.search).get('mt');

  useTitle('Главная страница');

  useEffect(() => {
    window.scroll(0, 0);
    return () => {
      dispatch(coreHideNotices());
    };
  }, [dispatch]);

  const defineIsDesktop = () => {
    if (window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches) {
      return true;
    }
    return false;
  };

  const { knmNonExistentQr } = useParams<{ knmNonExistentQr: string }>();
  const [isQrModalOpen, setIsQrModalOpen] = useState(!!knmNonExistentQr);

  const qrModalText = () => {
    if (knmNonExistentQr === ':knmNonExistentQr')
      return 'Паспорт мероприятия не сформирован, проведение контрольного (надзорного) мероприятия невозможно';

    if (knmNonExistentQr === ':pmNonExistentQr')
      return 'Паспорт мероприятия не сформирован, проведение профилактического мероприятия невозможно';

    return '';
  };

  useEffect(() => {
    if (isQrModalOpen) {
      window.history.replaceState(null, '', ROUTE_PORTAL);
    }
  }, [isQrModalOpen]);

  const onModalClose = () => setIsQrModalOpen(false);

  const isDesktop = defineIsDesktop();

  const goToSearch = () => history.push(ROUTE_SEARCH);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => dispatch(searchSetSearchString(e.target.value));

  const handleOpenDataButtonClick = () => history.push(ROUTE_OPEN_DATA);

  const handleSubmitForm = () => {
    goToSearch();
    if (!captchaToken) {
      dispatch(captchaSetActionAfterTokenLoad(searchGetResultData));
      dispatch(captchaSetIsActive(true));
    } else {
      dispatch(searchGetResultData());
    }
  };

  const renderForm = ({ handleSubmit, values }: FormRenderProps<IPublicMainForm>) => {
    return (
      <form className={styles.SearchForm} onSubmit={handleSubmit}>
        <TextInputField
          className={styles.SearchInput}
          type="text"
          name="searchString"
          id="searchInput"
          placeholder="Поиск КНМ"
          autoFocus={isDesktop}
          validate={composeValidators(
            makeMinCharactersValidator(3, 'Минимальная длина строки не соответствует требуемому значению'),
            makeMaxCharactersValidator(512, 'Превышена допустимая длина строки'),
          )}
          onChange={handleSearchInputChange}
        />
        <Button
          className={styles.SearchSubmitButton}
          color="primary"
          type="submit"
          disabled={!values?.searchString}
          text="Искать"
        />
      </form>
    );
  };

  return (
    <Layout>
      <div className={styles.Search}>
        <div className={styles.SearchBody}>
          <h1 className={styles.SearchTitle}>Поиск контрольных (надзорных) и профилактических мероприятий</h1>
          <Form<IPublicMainForm> onSubmit={handleSubmitForm} render={renderForm} />
          <div className={styles.LinkWrap}>
            <Link to={ROUTE_SEARCH} className={styles.Link}>
              Расширенный поиск
            </Link>
          </div>
        </div>
      </div>
      {knmNonExistentQr && (
        <PublicMainNonExistentQrModal qrModalText={qrModalText()} onClose={onModalClose} visible={isQrModalOpen} />
      )}
      <div className={styles.Content}>
        <PublicMainLawInfo className={styles.LawInfo} />
        <NewsContainer className={styles.News} locationMainPage />
        <div className={styles.OpenData}>
          <h2 className={styles.OpenDataTitle}>Открытые данные</h2>
          <button className={cn(styles.OpenDataButton)} onClick={handleOpenDataButtonClick}>
            Перейти к открытым данным
          </button>
        </div>
      </div>
      {mt && <PublicationWarningModal mt={mt} onClose={() => history.push(ROUTE_PORTAL)} />}
    </Layout>
  );
};

const mapStateToProps = (state: IRootState): IPublicMainPageStoreProps => ({
  captchaToken: getCaptchaToken(state),
});

export const PublicMainPage = connect(mapStateToProps)(PublicMainPageWithoutStore);
